import { Calendar, FileText, CheckSquare, AlertTriangle, Shield, Gavel, FileCheck, RefreshCw, XCircle, AlertCircle } from 'lucide-react';

export const PROCEDURE_TYPES = {
    // Contencioso
    OPPOSITION: "opposition",
    OPPOSITION_RESPONSE: "opposition_response",
    CANCELLATION_NON_USE: "cancellation_non_use",
    NULLITY: "nullity",
    ADMINISTRATIVE_INFRINGEMENT: "administrative_infringement",

    // Solicitud y registro
    REGISTRATION_APPLICATION: "registration_application",

    // Mantenimiento
    DECLARATION_OF_USE: "declaration_of_use",
    RENEWAL: "renewal",

    // Trámites
    OFFICE_ACTION: "office_action",
    LICENSE_OR_ASSIGNMENT: "license_or_assignment",
    EXPIRATION: "expiration",

    // Litigio
    INFRINGEMENT: "infringement",
    TFJA: "tfja",
    AMPARO: "amparo",
    ADMINISTRATIVE: "administrative",

    OTHER: "other"
};

export const PROCEDURE_LABELS = {
    [PROCEDURE_TYPES.OPPOSITION]: "Oposición",
    [PROCEDURE_TYPES.OPPOSITION_RESPONSE]: "Contestación de Oposición",
    [PROCEDURE_TYPES.CANCELLATION_NON_USE]: "Caducidad / Cancelación",
    [PROCEDURE_TYPES.NULLITY]: "Nulidad",
    [PROCEDURE_TYPES.ADMINISTRATIVE_INFRINGEMENT]: "Infracción Administrativa",
    [PROCEDURE_TYPES.INFRINGEMENT]: "Infracción",
    [PROCEDURE_TYPES.REGISTRATION_APPLICATION]: "Solicitud de Registro",
    [PROCEDURE_TYPES.DECLARATION_OF_USE]: "Declaración de Uso",
    [PROCEDURE_TYPES.RENEWAL]: "Renovación",
    [PROCEDURE_TYPES.OFFICE_ACTION]: "Contestación de Oficio",
    [PROCEDURE_TYPES.LICENSE_OR_ASSIGNMENT]: "Inscripción de Licencia/Cesión",
    [PROCEDURE_TYPES.EXPIRATION]: "Caducidad Administrativa",
    [PROCEDURE_TYPES.TFJA]: "TFJA",
    [PROCEDURE_TYPES.AMPARO]: "Amparo",
    [PROCEDURE_TYPES.ADMINISTRATIVE]: "Administrativo",
    [PROCEDURE_TYPES.OTHER]: "Otro Procedimiento"
};

export const PROCEDURE_COLORS = {
    [PROCEDURE_TYPES.OPPOSITION]: "text-red-700 bg-red-50 border-red-200",
    [PROCEDURE_TYPES.OPPOSITION_RESPONSE]: "text-orange-700 bg-orange-50 border-orange-200",
    [PROCEDURE_TYPES.REGISTRATION_APPLICATION]: "text-blue-700 bg-blue-50 border-blue-200",
    [PROCEDURE_TYPES.RENEWAL]: "text-green-700 bg-green-50 border-green-200",
    [PROCEDURE_TYPES.OFFICE_ACTION]: "text-purple-700 bg-purple-50 border-purple-200",
    [PROCEDURE_TYPES.DECLARATION_OF_USE]: "text-teal-700 bg-teal-50 border-teal-200",
    [PROCEDURE_TYPES.OTHER]: "text-gray-700 bg-gray-50 border-gray-200"
};

export const PROCEDURE_ICONS = {
    [PROCEDURE_TYPES.OPPOSITION]: Shield,
    [PROCEDURE_TYPES.OPPOSITION_RESPONSE]: Shield,
    [PROCEDURE_TYPES.REGISTRATION_APPLICATION]: FileText,
    [PROCEDURE_TYPES.RENEWAL]: RefreshCw,
    [PROCEDURE_TYPES.OFFICE_ACTION]: AlertCircle,
    [PROCEDURE_TYPES.DECLARATION_OF_USE]: CheckSquare,
    [PROCEDURE_TYPES.NULLITY]: Gavel,
    [PROCEDURE_TYPES.CANCELLATION_NON_USE]: XCircle,
    [PROCEDURE_TYPES.OTHER]: FileText
};

export const PROCEDURE_TEMPLATES = {
    [PROCEDURE_TYPES.OPPOSITION]: {
        label: "Oposición",
        fields: [
            { key: "opposition_number", label: "Número de Oposición", type: "text", placeholder: "Ej. 12345/2024" },
            { key: "basis", label: "Fundamento Legal", type: "textarea", placeholder: "Ej. Confusión gráfica con marca..." },
            { key: "counterparty_representative", label: "Representante Contraparte", type: "text" }
        ],
        suggestedTasks: [
            { title: "Analizar escrito de oposición", priority: "HIGH", description: "Revisar argumentos y pruebas presentadas." },
            { title: "Preparar contestación", priority: "URGENT", description: "Redactar argumentos de defensa." },
            { title: "Presentar contestación ante IMPI", priority: "URGENT" }
        ]
    },
    [PROCEDURE_TYPES.OPPOSITION_RESPONSE]: {
        label: "Contestación de Oposición",
        fields: [
            { key: "opposition_number", label: "Número de Oposición", type: "text" },
            { key: "arguments_summary", label: "Resumen de Argumentos", type: "textarea" }
        ],
        suggestedTasks: [
            { title: "Recopilar pruebas de uso", priority: "HIGH" },
            { title: "Redactar contestación", priority: "HIGH" }
        ]
    },
    [PROCEDURE_TYPES.OFFICE_ACTION]: {
        label: "Contestación de Oficio",
        fields: [
            { key: "office_action_number", label: "Número de Oficio", type: "text" },
            { key: "cited_anteriorities", label: "Anterioridades Citadas", type: "textarea", placeholder: "Lista de expedientes citados..." },
            { key: "objection_type", label: "Tipo de Objeción", type: "select", options: ["none", "Anterioridad", "Impedimento Legal", "Requisitos Formales"] }
        ],
        suggestedTasks: [
            { title: "Analizar anterioridades citadas", priority: "HIGH" },
            { title: "Reportar oficio al cliente", priority: "URGENT" },
            { title: "Preparar escrito de respuesta", priority: "MEDIUM" }
        ]
    },
    [PROCEDURE_TYPES.RENEWAL]: {
        label: "Renovación",
        fields: [
            { key: "renewal_number", label: "Número de Solicitud", type: "text" },
            { key: "products_in_use", label: "Productos en Uso", type: "textarea", placeholder: "Productos espcíficos que se declararán..." }
        ],
        suggestedTasks: [
            { title: "Verificar uso real de la marca", priority: "MEDIUM" },
            { title: "Solicitar pago de derechos", priority: "MEDIUM" },
            { title: "Presentar solicitud de renovación", priority: "HIGH" }
        ]
    },
    [PROCEDURE_TYPES.DECLARATION_OF_USE]: {
        label: "Declaración de Uso",
        fields: [
            { key: "products_declared", label: "Productos a Declarar", type: "textarea" },
            { key: "use_date", label: "Fecha de Primer Uso (si aplica)", type: "date" }
        ],
        suggestedTasks: [
            { title: "Recopilar facturas/pruebas", priority: "MEDIUM" },
            { title: "Preparar declaración", priority: "MEDIUM" }
        ]
    },
    [PROCEDURE_TYPES.REGISTRATION_APPLICATION]: {
        label: "Solicitud de Registro",
        fields: [
            { key: "application_number", label: "Número de Expediente", type: "text" },
            { key: "nice_classes", label: "Clases Niza", type: "text", placeholder: "Ej. 9, 35, 42" }
        ],
        suggestedTasks: [
            { title: "Realizar búsqueda fonética", priority: "MEDIUM" },
            { title: "Preparar solicitud", priority: "HIGH" },
            { title: "Pago de derechos", priority: "HIGH" }
        ]
    }
};

export const getProcedureTemplate = (type) => {
    return PROCEDURE_TEMPLATES[type] || {
        label: PROCEDURE_LABELS[type] || "Procedimiento",
        fields: [],
        suggestedTasks: []
    };
};
